import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CustomerHeader from '../../components/ui/CustomerHeader';
import CartItem from './components/CartItem';
import OrderSummary from './components/OrderSummary';
import RecommendedProducts from './components/RecommendedProducts';
import RecentlyViewed from './components/RecentlyViewed';
import EmptyCart from './components/EmptyCart';
import Footer from '../../components/ui/Footer';
import { getProducts, fromWcProduct } from '../../lib/wp-api';

const ShoppingCart = () => {
  const navigate = useNavigate();
  // Cart state is canonical in CartContext (persisted to localStorage).
  // We just derive cartItems from cart.items so existing render code keeps working.
  const cart = useCart();
  const wishlist = useWishlist();
  const cartItems = cart.items;
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);


  // Recommended products — pulled from WC (popular trending items). Same
  // server-side filter pattern as the catalog uses.
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getProducts({ per_page: 4, orderby: 'popularity' })
      .then((rows) => { if (!cancelled) setRecommendedProducts(rows.map(fromWcProduct)); })
      .catch(() => { if (!cancelled) setRecommendedProducts([]); });
    return () => { cancelled = true; };
  }, []);


  // Mock recently viewed products
  const recentlyViewedProducts = [];


  // Mock popular products for empty cart
  const [popularProducts, setPopularProducts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getProducts({ per_page: 4, tag: 'featured' })
      .then(items => {
        if (cancelled) return;
        setPopularProducts(items.map(p => ({
          id: p.id,
          name: p.name,
          brand: '',
          price: p.price,
          image: p.image,
          imageAlt: p.name,
          isPopular: true,
        })));
      })
      .catch(() => { /* silent: section just stays empty + hidden */ });
    return () => { cancelled = true; };
  }, []);


  useEffect(() => {
    // Simulate loading cart data
    const loadCartData = async () => {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Failed to load cart data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCartData();
  }, []);

  const handleUpdateQuantity = (itemId, newQuantity) => {
    cart.updateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = (itemId) => {
    cart.removeItem(itemId);
  };

  const handleMoveToWishlist = (itemId) => {
    const item = cartItems?.find((it) => it?.id === itemId);
    if (!item) return;
    wishlist.toggle(item);
    cart.removeItem(itemId);
  };

  const handleAddToCart = (product) => {
    if (!product) return;
    cart.addItem(product, 1);
  };

  const handleApplyPromoCode = (promoCode) => {
    console.log('Applied promo code:', promoCode);
    // Handle promo code application logic
  };

  const handleProceedToCheckout = () => {
    navigate('/checkout');
  };

  // Calculate totals
  const subtotal = cartItems?.reduce((sum, item) => sum + item?.price * item?.quantity, 0);
  const shipping = subtotal >= 50 ? 0 : 9.99;
  const tax = subtotal * 0.08;
  const discount = 0; // Will be updated when promo codes are applied
  const total = subtotal + shipping + tax - discount;

  if (isLoading) {
    return (
      <div className="bg-background min-h-screen">
        <CustomerHeader
          cartItemCount={0}
          onAuthClick={() => navigate('/login')}
          onCartClick={() => navigate('/cart')} />

        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your cart...</p>
          </div>
        </div>
      </div>);

  }

  if (cartItems?.length === 0) {
    return (
      <div className="bg-background min-h-screen">
        <CustomerHeader
          cartItemCount={0}
          onAuthClick={() => navigate('/login')}
          onCartClick={() => navigate('/cart')} />

        <EmptyCart
          recentlyViewed={recentlyViewedProducts}
          popularProducts={popularProducts} />

      </div>);

  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader
        cartItemCount={cartItems?.length || 0}
        onCartClick={() => {}}
      />

      <div className="pt-20 pb-16 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  Shopping Cart
                </h1>
                <p className="text-muted-foreground mt-2">
                  {cartItems?.length} {cartItems?.length === 1 ? 'item' : 'items'} in your cart
                </p>
              </div>
              
              <Button
                variant="outline"
                onClick={() => navigate('/product-catalog')}
                iconName="ArrowLeft"
                iconPosition="left">

                Continue Shopping
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cartItems?.map((item) =>
              <CartItem
                key={item?.id}
                item={item}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveItem={handleRemoveItem}
                onMoveToWishlist={handleMoveToWishlist} />

              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <OrderSummary
                subtotal={subtotal}
                shipping={shipping}
                tax={tax}
                discount={discount}
                total={total}
                onApplyPromoCode={handleApplyPromoCode}
                onProceedToCheckout={handleProceedToCheckout}
                isCheckoutLoading={isCheckoutLoading} />

            </div>
          </div>

          {/* Recently Viewed */}
          {recentlyViewedProducts?.length > 0 &&
          <div className="mt-16">
              <RecentlyViewed
              products={recentlyViewedProducts}
              onAddToCart={handleAddToCart} />

            </div>
          }

          {/* Recommended Products */}
          <div className="mt-16">
            <RecommendedProducts
              products={recommendedProducts}
              onAddToCart={handleAddToCart} />

          </div>

          {/* Trust Signals */}
          <div className="mt-16 bg-card border border-border rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mb-3">
                  <Icon name="Shield" size={24} className="text-success" />
                </div>
                <h3 className="font-medium text-foreground mb-1">Secure Checkout</h3>
                <p className="text-sm text-muted-foreground">SSL encrypted payment</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                  <Icon name="Truck" size={24} className="text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-1">Free Shipping</h3>
                <p className="text-sm text-muted-foreground">On orders over $50</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mb-3">
                  <Icon name="RotateCcw" size={24} className="text-warning" />
                </div>
                <h3 className="font-medium text-foreground mb-1">Easy Returns</h3>
                <p className="text-sm text-muted-foreground">30-day return policy</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-3">
                  <Icon name="Headphones" size={24} className="text-accent" />
                </div>
                <h3 className="font-medium text-foreground mb-1">24/7 Support</h3>
                <p className="text-sm text-muted-foreground">Customer service</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* ... keep existing modals ... */}
    </div>
  );
};

export default ShoppingCart;